import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { CORE_TEAM_DOMAIN, useAuth } from '../contexts/AuthContext'
import { NeonLogoExplorations } from '../components/NeonWordmark'

const EARTH_TOKENS = [
  ['--earth-bg', '#F5F1EA', 'raw silk / limestone wash'],
  ['--earth-surface', '#ECE5D8', 'sun-warmed paper'],
  ['--earth-surface-raised', '#E2D9C8', 'raised paper surface'],
  ['--earth-border', '#CFC4B0', 'quiet card/form border'],
  ['--earth-text-primary', '#2E2B26', 'charred teak'],
  ['--earth-text-secondary', '#5C5348', 'warm body copy'],
  ['--earth-text-muted', '#8C8173', 'labels and metadata only'],
  ['--earth-accent', '#6B7A5E', 'fern shadow'],
  ['--earth-accent-deep', '#4A5644', 'deep fern'],
] as const

const GLOW_TOKENS = [
  ['--glow-primary', '#E88D2A', 'golden hour on water'],
  ['--glow-secondary', '#D4702A', 'terracotta path'],
  ['--glow-warm', '#F2A858', 'balcony wall bounce'],
  ['--glow-coral', '#E36A54', 'Gulmohar at dusk'],
  ['--glow-deep', '#B04E2B', 'deep ember'],
  ['--glow-ocean', '#5A7282', 'Mount Lavinia bay at dusk'],
  ['--glow-horizon', '#F5B366', 'sun-haze horizon'],
  ['--glow-dusk', '#8F6B8E', 'cloud-band mauve accent only'],
] as const

const BRAND_GUIDELINES = [
  {
    title: 'Brand Mark',
    items: [
      'The physical neon wordmark is the logotype, not decoration.',
      'Production plane can use saturated neon: video, cold-open title cards, episode thumbnails, and the physical set.',
      'Digital plane translates the neon through Bawa Earth + Sundown Glow tokens. Do not use raw neon colors in UI chrome.',
    ],
  },
  {
    title: 'Typography',
    items: [
      'Primary font: Inter variable, falling back to system-ui, -apple-system, Segoe UI, Roboto, sans-serif.',
      'Sinhala pair: Noto Sans Sinhala cross-platform, Iskoola Pota on Windows. Match Sinhala size to adjacent English cadence.',
      'Headings use semibold 600 with restrained tracking. No decorative, script, typewriter, or editorial display faces.',
    ],
  },
  {
    title: 'Type Scale',
    items: [
      'Hero title: 48px / --text-5xl. Page H1: 36px / --text-4xl. Large H2: 30px / --text-3xl.',
      'Section H2: 24px / --text-2xl. H3: 20px / --text-xl. Body: 16px / --text-base with 1.6 line-height.',
      'Labels and metadata use 12px / --text-xs, uppercase sparingly, with generous tracking.',
    ],
  },
  {
    title: 'Surface Rules',
    items: [
      'Working surfaces use Bawa Earth: admin, forms, documentation, tables, notifications, and dashboards.',
      'Working CMS surfaces must use solid token backgrounds only. No semi-transparent light/grey mid-background washes over forms, buttons, or body text.',
      'Branding surfaces may use Sundown Glow: public hero, platform cards, thumbnails, and first-impression moments.',
      '--glow-dusk is accent-only: no body text, no gradients, no chrome fills, and no more than roughly 3% of a viewport.',
    ],
  },
]

const TYPOGRAPHY_USAGE = [
  {
    surface: 'Global Body',
    font: 'Inter / --font-body',
    size: '16px / --text-base',
    rule: 'Line-height 1.6. Use --earth-text-secondary for body support copy only when contrast remains clear.',
  },
  {
    surface: 'Page H1',
    font: 'Inter / --font-display',
    size: '36px / --text-4xl',
    rule: 'Weight 600, tracking around -0.02em. Used for admin, notifications, team docs, public section headers.',
  },
  {
    surface: 'Hero Title',
    font: 'Inter / --font-display',
    size: 'Clamp to 2rem-3.4rem on public hero panel',
    rule: 'Tighter tracking is allowed only on hero/branding surfaces. Keep white text over dark scrim.',
  },
  {
    surface: 'Sinhala',
    font: 'Noto Sans Sinhala / Iskoola Pota',
    size: '1.06em inline, 16-22px in hero poem',
    rule: 'Use .si or --font-sinhala. Match English cadence; do not make Sinhala feel like a separate decorative script.',
  },
  {
    surface: 'Eyebrows & Metadata',
    font: 'Inter / --font-sans',
    size: '12px / --text-xs',
    rule: 'Uppercase, 0.12-0.18em tracking. Color should read as metadata, not disabled text.',
  },
  {
    surface: 'Buttons & Controls',
    font: 'Inter / --font-sans',
    size: '14px / --text-sm',
    rule: 'Weight 600. Primary uses --glow-primary on #1A1814 text. Secondary keeps Earth border and readable text.',
  },
  {
    surface: 'Markdown Reader',
    font: 'Inter / --font-body',
    size: '18px body, 44px reader H1',
    rule: 'Reader is always light Bawa paper for contrast. Long-form text uses #3A352E, not muted metadata grey.',
  },
]

const DESIGN_DOCS = [
  {
    slug: 'current-design-brief',
    title: 'Current Yan-Vada design brief',
    path: 'Yan-Vada Hero Pilot/design-brief.md',
    note: 'Current reference for brand mark, palette, typography, spacing, prohibitions, and built hero direction.',
    markdown: `# Yan-Vada — Design Brief

## 1. What Yan-Vada is

Sri Lankan podcast. Video-first. Long-form conversational. Host: Riz Razak. Recorded on a fifth-floor south-facing balcony in Mount Lavinia overlooking the Indian Ocean. Golden hour is the temporal and visual spine.

## 2. Voice

- Meaningful. Specificity over platitude. Named things over categories.
- Fun. Not cynical. Not self-serious. Playful mid-argument.
- Unfiltered. Says what it means. No marketing register. Sounds like Riz speaking aloud.

Register: Sri Lankan English. Shorter sentences. Comfortable code-switching with Sinhala where English dilutes meaning.

## 3. The brand mark — physical neon

The Yan-Vada identity is the physical neon wordmark installed on the balcony. It is not decorative; it is the logotype.

- On set: the real neon appears in every episode.
- Digital: use the SVG neon wordmark treatment only where it belongs.
- Archived explorations: classic glass-tube, monochrome dusk, dual-plane, clean geometric are references, not canon.

Rules:

- Human + neon + sunset + ocean must feel like one continuous brand frame.
- Do not recolor the wordmark outside the palette translation rule.
- Do not stretch, skew, proportion-distort, or add digital-plane outer glows.

## 4. Digital Plane Palette

\`\`\`css
--earth-bg: #F5F1EA             raw silk / limestone wash
--earth-surface: #ECE5D8        sun-warmed paper
--earth-text-primary: #2E2B26   charred teak
--earth-text-secondary: #5C5348 warm body copy
--earth-text-muted: #8C8173     labels and metadata only
--earth-accent: #6B7A5E         fern shadow

--glow-primary: #E88D2A         golden hour on water
--glow-coral: #E36A54           Gulmohar at dusk
--glow-ocean: #5A7282           Mount Lavinia bay at dusk
--glow-dusk: #8F6B8E            cloud-band mauve + bougainvillea
\`\`\`

## 5. Typography

- Primary: Inter variable.
- Fallback: system-ui, -apple-system, Segoe UI, Roboto, sans-serif.
- Sinhala pair: Noto Sans Sinhala / Iskoola Pota.
- Headings: 600 weight, slight tight tracking, restrained hierarchy.
- Body: 16px, line-height 1.6.

## 6. Prohibitions

- No saturated neon on the digital plane.
- No purple except \`--glow-dusk\` under accent-only constraints.
- No generic sunset gradients.
- No semi-transparent light/grey background washes on working CMS surfaces. They obscure controls in dark mode; use solid Bawa Earth tokens instead.
- No editorial meta-framing or corporate pitch-deck tone.
- No AI-vocabulary: leverage, synergy, delve, unleash, game-changing.`,
  },
  {
    slug: 'admin-cms-brief',
    title: 'Admin CMS Claude Design brief',
    path: '07-execution/CLAUDE-DESIGN-ADMIN-CMS-BRIEF.md',
    note: 'Current upload brief for admin/member CMS, notifications, hero media controls, and live notification behavior.',
    markdown: `# Claude Design Brief — Admin CMS

## Product Job

Create a high-fidelity admin/CMS panel for Yan-Vada that lets Riz and approved admins manage member access after login is working.

The panel is internal. It should feel calm, legible, and operational. No SaaS-dashboard sameness. No streaming-platform dark UI.

## Functional Scope

- List all members and access requests.
- Show total, pending, and approved counts.
- Pre-approve or invite a new email.
- Edit role, status, sponsor access, bundle, and rights overrides.
- Support Approve + email link for pending requests.
- Keep destructive and low-frequency actions folded into Settings.
- Surface member notifications for access requests, role changes, and operational updates.
- Manage public-home hero media.

## Screens To Mock

- Members overview with summary cards, invite card, and member list cards.
- Pending request review queue above approved members.
- Member settings for archive, restore, audit trail, or future advanced notes.
- Advanced access with inherited bundle rights versus manual overrides.
- Member notifications with new, viewed, to-do, done, and context states.
- Public home content and hero media controls.

## Visual Direction

Use Bawa Earth for working surfaces. Use Sundown Glow as a restrained accent. Admin copy must be practical, not lyrical.

Working CMS surfaces must use solid Bawa Earth token backgrounds. Do not place semi-transparent light/grey mid-background washes behind form fields, buttons, or body text; they obscure controls in dark mode.

## Notification Icon

- Custom line bell, not a stock filled bell.
- Count badge uses \`--glow-primary\`.
- Seen items quiet down.
- To-do items remain clearly actionable until marked done.`,
  },
  {
    slug: 'master-brief',
    title: 'Master Claude Design brief',
    path: '07-execution/F1-CLAUDE-DESIGN-MASTER-BRIEF.md',
    note: 'Master visual system brief with two-plane palette rules, typography, and dark-mode discipline.',
    markdown: `# F1 — Claude Design Master Session Brief

## Identity

Yan-Vada (යන්-වාද) is a Sri Lankan podcast. Video-first. Long-form conversational. It is recorded on a Mount Lavinia balcony overlooking the Indian Ocean at golden hour.

## Canonical Brand Mark — The Neon

The Yan-Vada identity is a physical neon wordmark installed on the balcony where every episode records. It is not set dressing. It is the logotype.

Every Yan-Vada episode composes human subject + neon wordmark + Mount Lavinia sunset + Indian Ocean in one continuous frame. Digital output must feel like it belongs alongside this frame.

## Two-Plane Rule

Production plane: cold-open title card, episode thumbnails, and the physical set. Six saturated glyph colors plus white hyphen are allowed here.

Digital plane: pitch pages, PWA chrome, social graphics, emails, and rendered UI. Saturation translates through Bawa Earth base + Sundown Glow overlay.

## Typography

- Primary: Inter variable font.
- Fallback: system-ui, -apple-system, Segoe UI, Roboto, sans-serif.
- Sinhala pair: Iskoola Pota, Noto Sans Sinhala.
- Body line-height: 1.6.
- Headings: semibold 600, slight tight tracking.
- No decorative fonts. No script. No typewriter. Inter carries the full voice.

## Dark Mode Discipline

Dark mode is independently designed, not inverted. Reference concept: Mount Lavinia balcony at night — post-sunset ocean, faint starlight, oil lamp warmth, dim horizon trace.

Disallowed: Twitch, Spotify, Apple Music, Discord, gaming, or streaming-platform aesthetics. No electric purple, neon green-on-black, or hot magenta against void black.`,
  },
  {
    slug: 'design-issues',
    title: 'Design issues queue',
    path: '05-deliberations/DESIGN-ISSUES-QUEUE.md',
    note: 'Ridma/design backlog for admin density, mobile states, bells, gates, and UX polish.',
    markdown: `# Design Issues Queue

## Active Themes

- Admin density needs to stay readable. Do not turn member management into a generic SaaS dashboard.
- Mobile admin states need one-handed form controls and no horizontal scrolling.
- Bell and notification motion should stay quiet: count pulse, subtle badge, no toast spam.
- Rights panels need a clear split between inherited bundle rights and manual overrides.
- Hero media controls need to feel like publish controls, not a full media library.

## Current Design Fixes

- Frequent admin work stays visible.
- Destructive and rare actions fold into Settings.
- Notification states now separate new, seen, viewed, to-do, and done.
- Internal design docs live under DGTL Team and should read as reference material, not public marketing.

## Open Questions

- Should pending requests be pinned above approved members?
- Should admin routes eventually get a left rail?
- How much live-notification animation is enough without becoming distracting?
- How should access audit trail surface without overcrowding member cards?`,
  },
  {
    slug: 'session-stocktake',
    title: 'Session 17 council stocktake',
    path: '07-execution/SESSION-17-COUNCIL-STOCKTAKE.md',
    note: 'Current council and steering decisions, immediate fixes, and design/back-end backlog.',
    markdown: `# Session 17 Council Stocktake

## Recent Shipped Work

- Public home and home-content CMS.
- Admin members CMS with access bundles, rights overrides, and founder lock.
- Notification centre with seen, read, to-do, and done states.
- Integration delivery queue placeholders for Slack and Notion.
- DGTL Team design docs route.
- Admin member latency fixes: batched rights lookup, local row updates, and reduced quiet polling.

## Steering Constraints

- Yan-Vada data stays isolated in \`yan_vada_*\` tables.
- Supabase service-role access stays server-side only.
- Public pages cannot expose sponsor pitch pricing/proposal content.
- Slack/Notion delivery workers remain deferred until transactional hardening.

## Design Carry-Forward

- Keep working surfaces Bawa Earth.
- Use Sundown Glow sparingly.
- Do not let dark mode drift into streaming-platform chrome.
- Keep design references readable inside the PWA rather than dead file paths.`,
  },
  {
    slug: 'neon-reconciliation',
    title: 'Neon reconciliation',
    path: '05-deliberations/SG1-D9-NEON-RECONCILIATION.md',
    note: 'Explains how the physical neon logo maps into the restrained digital-plane palette.',
    markdown: `# SG1-D9 — Neon Reconciliation

## Origin

The canonical neon wordmark uses saturated colors, including magenta/purple. Earlier Sundown Glow rules prohibited purple and neon saturation on digital surfaces. SG1-D9 resolves that tension.

## Two-Plane Rule

Production plane and digital plane carry different saturation constraints.

- Production plane: physical set, recorded video, cold-open title cards, and episode thumbnails. Saturated neon is allowed because it exists in the real recorded environment.
- Digital plane: PWA chrome, pitch pages, social graphics, email templates, and rendered UI. Saturation is translated through Bawa Earth + Sundown Glow.

## Token Mapping

Seven neon signals already map to existing Earth/Glow tokens. The missing translation is the magenta/purple glyph.

\`\`\`css
--glow-dusk: #8F6B8E /* cloud-band mauve + bougainvillea, Mt Lavinia dusk */
\`\`\`

## Usage Constraints

- Single accent only: glyph fill, heading underline, icon dot, or watermark.
- No gradients using \`--glow-dusk\`.
- No nav, button, page, or card fills.
- No body text because contrast is below WCAG AA body threshold.
- Keep total usage under roughly 3% of any viewport.

## Practical Read

The neon is the identity in video. The PWA should feel like it belongs beside that balcony frame, but it should not become a nightclub, gaming, or streaming-platform interface.`,
  },
]

type MarkdownBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'code'; text: string }

function parseMarkdown(markdown: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = []
  const lines = markdown.trim().split('\n')
  let paragraph: string[] = []
  let list: string[] = []
  let code: string[] = []
  let inCode = false

  function flushParagraph() {
    if (!paragraph.length) return
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') })
    paragraph = []
  }

  function flushList() {
    if (!list.length) return
    blocks.push({ type: 'list', items: list })
    list = []
  }

  for (const line of lines) {
    if (line.startsWith('```')) {
      if (inCode) {
        blocks.push({ type: 'code', text: code.join('\n') })
        code = []
        inCode = false
      } else {
        flushParagraph()
        flushList()
        inCode = true
      }
      continue
    }

    if (inCode) {
      code.push(line)
      continue
    }

    if (!line.trim()) {
      flushParagraph()
      flushList()
      continue
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/)
    if (heading) {
      flushParagraph()
      flushList()
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] })
      continue
    }

    if (line.startsWith('- ')) {
      flushParagraph()
      list.push(line.slice(2))
      continue
    }

    paragraph.push(line.trim())
  }

  flushParagraph()
  flushList()
  return blocks
}

function MarkdownPreview({ markdown }: { markdown: string }) {
  const blocks = useMemo(() => parseMarkdown(markdown), [markdown])
  return (
    <div className="team-markdown-preview">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const Heading = block.level === 1 ? 'h3' : block.level === 2 ? 'h4' : 'h5'
          return <Heading key={index}>{block.text}</Heading>
        }
        if (block.type === 'list') {
          return <ul key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ul>
        }
        if (block.type === 'code') {
          return <pre key={index}><code>{block.text}</code></pre>
        }
        return <p key={index}>{block.text}</p>
      })}
    </div>
  )
}

function TokenGrid({ title, tokens }: { title: string; tokens: readonly (readonly [string, string, string])[] }) {
  return (
    <section className="team-design-card">
      <h2>{title}</h2>
      <div className="team-token-grid">
        {tokens.map(([name, value, note]) => (
          <div key={name} className="team-token-row">
            <span className="team-token-swatch" style={{ background: value }} />
            <div>
              <strong>{name}</strong>
              <code>{value}</code>
              <p>{note}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default function TeamDesignPage() {
  const { isLoggedIn, loading, email, role, status } = useAuth()
  const [readerDocSlug, setReaderDocSlug] = useState<string | null>(null)
  const readerPanelRef = useRef<HTMLElement | null>(null)
  const readerCloseRef = useRef<HTMLButtonElement | null>(null)
  const readerOpenerRef = useRef<HTMLButtonElement | null>(null)
  const readerDoc = DESIGN_DOCS.find(doc => doc.slug === readerDocSlug) ?? null
  const readerOpen = readerDoc !== null
  const isDgtl = email?.toLowerCase().endsWith(`@${CORE_TEAM_DOMAIN}`) === true
  const canView = isLoggedIn && status === 'approved' && (isDgtl || ['founder', 'admin'].includes(role ?? ''))

  useEffect(() => {
    if (!readerOpen) return undefined
    const previousOverflow = document.body.style.overflow
    const restoreFocusTarget = readerOpenerRef.current
    document.body.style.overflow = 'hidden'
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setReaderDocSlug(null)
        return
      }
      if (event.key !== 'Tab') return
      const focusable = readerPanelRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable?.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    window.setTimeout(() => readerCloseRef.current?.focus(), 0)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      restoreFocusTarget?.focus()
    }
  }, [readerOpen])

  function openReader(event: React.MouseEvent<HTMLButtonElement>, slug: string) {
    readerOpenerRef.current = event.currentTarget
    setReaderDocSlug(slug)
  }

  if (loading) return <div className="team-design-page"><p className="admin-muted">Checking team access...</p></div>
  if (!isLoggedIn) return <Navigate to="/signin" replace />
  if (!canView) {
    return (
      <main className="team-design-page">
        <span className="legal-eyebrow">DGTL Team</span>
        <h1>Team access required.</h1>
        <p className="admin-muted">Design and palette references are available to approved DGTL/internal admins.</p>
        <Link className="btn btn-secondary" to="/">Back to public home</Link>
      </main>
    )
  }

  return (
    <main className="team-design-page">
      <header className="team-design-header">
        <span className="legal-eyebrow">DGTL Team · design system</span>
        <h1>Yan-Vada design and palette docs</h1>
        <p>
          Quick reference for the DGTL team: the live neon asset, current typography rules, source docs, and the Bawa Earth / Sundown Glow token system.
        </p>
      </header>

      <section className="team-design-card team-neon-asset-card">
        <div className="team-neon-copy">
          <span className="legal-eyebrow">Design asset</span>
          <h2>Canonical neon wordmark</h2>
          <p>
            This is the in-app SVG asset currently used in the public hero and sign-in brand panel. It is a production-plane neon treatment, so use it as a brand asset, not as a generic UI colour source.
          </p>
        </div>
        <NeonLogoExplorations />
      </section>

      <section className="team-design-card">
        <h2>Current Brand Guidelines</h2>
        <div className="team-guideline-grid">
          {BRAND_GUIDELINES.map(group => (
            <article key={group.title}>
              <h3>{group.title}</h3>
              <ul>
                {group.items.map(item => <li key={item}>{item}</li>)}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <div className="team-design-grid">
        <TokenGrid title="Bawa Earth Base" tokens={EARTH_TOKENS} />
        <TokenGrid title="Sundown Glow Overlay" tokens={GLOW_TOKENS} />
      </div>

      <section className="team-design-card">
        <h2>Typography & Title Usage</h2>
        <div className="team-type-table">
          {TYPOGRAPHY_USAGE.map(item => (
            <article key={item.surface}>
              <strong>{item.surface}</strong>
              <span>{item.font}</span>
              <span>{item.size}</span>
              <p>{item.rule}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="team-design-card">
        <h2>Design Sprint Sources</h2>
        <div className="team-doc-list">
          {DESIGN_DOCS.map(doc => (
            <button key={doc.path} className="team-doc-card" type="button" onClick={(event) => openReader(event, doc.slug)}>
              <span className="team-doc-open-label">Open reader</span>
              <h3>{doc.title}</h3>
              <p>{doc.note}</p>
              <code>{doc.path}</code>
            </button>
          ))}
        </div>
      </section>

      <section className="team-design-card">
        <h2>Current Sprint Notes</h2>
        <ul className="team-design-notes">
          <li>Frequent admin work stays visible; destructive and rare actions fold into Settings.</li>
          <li>Pending requests need a clear review queue above approved members.</li>
          <li>Notifications need live count updates, subtle pulse, seen/read/done states, and quiet dropdown motion.</li>
          <li>Hero media controls should stay simple: image/video toggle, upload/change video, save-to-publish.</li>
          <li>One focused review round is enough for small design polish; use extra rounds only for accessibility, auth/access, state mutation, data/schema, security, or deploy-risk changes.</li>
          <li>Do not drift into generic SaaS dashboards or streaming-platform dark palettes.</li>
        </ul>
      </section>

      {readerDoc && (
        <div className="team-reader-overlay" role="dialog" aria-modal="true" aria-labelledby="team-reader-title">
          <div className="team-reader-backdrop" onClick={() => setReaderDocSlug(null)} />
          <section ref={readerPanelRef} className="team-reader-panel">
            <header className="team-reader-header">
              <div>
                <span className="legal-eyebrow">Markdown reader</span>
                <h2 id="team-reader-title">{readerDoc.title}</h2>
                <code>{readerDoc.path}</code>
              </div>
              <button ref={readerCloseRef} className="team-reader-close" type="button" onClick={() => setReaderDocSlug(null)} aria-label="Close markdown reader">
                ×
              </button>
            </header>

            <div className="team-doc-tabs" aria-label="Markdown previews">
              {DESIGN_DOCS.map(doc => (
                <button
                  key={doc.slug}
                  type="button"
                  aria-selected={doc.slug === readerDoc.slug}
                  className={doc.slug === readerDoc.slug ? 'active' : ''}
                  onClick={() => setReaderDocSlug(doc.slug)}
                >
                  {doc.title}
                </button>
              ))}
            </div>

            <div className="team-reader-body">
              <MarkdownPreview markdown={readerDoc.markdown} />
            </div>
          </section>
        </div>
      )}
    </main>
  )
}
